import { streamLainAnimationController } from '@blocksuite/affine/shared/utils';
import clsx from 'clsx';
import type { LottieRef } from 'lottie-react';
import { useEffect, useRef, useState } from 'react';

import { LazyLottie } from './lazy-lottie';
import * as styles from './styles.css';

const loadAnimation = () => import('./folder-icon.json');

export interface FolderIconProps {
  open: boolean;
  className?: string;
  speed?: number;
}

/** Lottie supplies frames only; the canonical controller owns their timeline. */
export const AnimatedFolderIcon = ({ open, className }: FolderIconProps) => {
  const host = useRef<HTMLSpanElement>(null);
  const lottieRef: LottieRef = useRef(null);
  const [ready, setReady] = useState(false);
  const frame = useRef(0);
  const initialized = useRef(false);

  useEffect(() => {
    const element = host.current;
    const lottie = lottieRef.current;
    if (!ready || !element || !lottie) return;
    const lastFrame = Math.max(0, (lottie.getDuration(true) ?? 1) - 1);
    const target = open ? lastFrame : 0;
    const update = (value: number) => {
      frame.current = value;
      lottie.goToAndStop(value, true);
    };
    if (!initialized.current) {
      initialized.current = true;
      update(target);
      return;
    }
    const from = frame.current;
    const handle = streamLainAnimationController.animateProgress(
      element,
      progress => update(from + (target - from) * progress)
    );
    return () => handle.cancel();
  }, [open, ready]);

  return (
    <span
      ref={host}
      className={clsx(styles.root, className)}
      aria-hidden="true"
    >
      <LazyLottie
        autoPlay={false}
        loop={false}
        lottieRef={lottieRef}
        load={loadAnimation}
        onDOMLoaded={() => setReady(true)}
      />
    </span>
  );
};
